import { BotBase } from './botBase';

import type {
	CardObject,
	Command,
	GameState,
	Manager,
	PassCommand,
	PlayCommand,
} from '../lib';
import {
	assumeType,
	compareRank,
	GameAction,
	getCardObjectFromString,
	getRandomItem,
	getTrickWinner,
	makeNextStageCommand,
	Rank,
	RoundStage,
	Suit,
} from '../lib';

//This bot just plays the lowest possible card. It also tries to pass the Queen of Spades and aces

function getBetterPassCandidate(previousCandidate: PassCommand, nextCandidate: PassCommand): PassCommand {
	const previousHasQueen = previousCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.suit === Suit.Spades && card.rank === Rank.Queen;
	}).length > 0;

	const nextHasQueen = nextCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.suit === Suit.Spades && card.rank === Rank.Queen;
	}).length > 0;

	if (previousHasQueen && !nextHasQueen) {
		return previousCandidate;
	} else if (nextHasQueen && !previousHasQueen) {
		return nextCandidate;
	}

	const previousAceCount = previousCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.rank === Rank.Ace;
	}).length;

	const nextAceCount = nextCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.rank === Rank.Ace;
	}).length;

	if (previousAceCount > nextAceCount) {
		return previousCandidate;
	}

	return nextCandidate;
}

function getPassCommand(commands: Command[]): Command {
	let candidateCommand: PassCommand = getRandomItem(commands) as PassCommand;

	if (commands.length > 1) {
		for (const command of commands) {
			assumeType<PassCommand>(command);

			candidateCommand = getBetterPassCandidate(candidateCommand, command);
		}
	}

	return candidateCommand;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getBetterPlayCandidate(previousCandidate: PlayCommand, nextCandidate: PlayCommand, gameState: GameState): PlayCommand {
	const currentRound = gameState?.rounds?.at(-1);
	const currentTrick = currentRound?.tricks?.at(-1);
	const previousCard = getCardObjectFromString(previousCandidate.data.card);
	const nextCard = getCardObjectFromString(nextCandidate.data.card);
	const winner = currentTrick ? getTrickWinner(currentTrick) : null;
	const winningCard = winner !== null && currentTrick?.cardsPlayed[winner] ? getCardObjectFromString(currentTrick?.cardsPlayed[winner]) : null;

	//play the queen of spades if there are higher cards in the trick
	if (nextCard.suit === Suit.Spades && nextCard.rank === Rank.Queen) {
		if (winningCard) {
			const willNextCardWinTrick = compareRank(nextCard.rank, winningCard.rank) > 0 && nextCard.suit === winningCard.suit;

			if (!willNextCardWinTrick) {
				return nextCandidate;
			}else{
				return previousCandidate;
			}
		}
	}

	//If there's nothing going on yet, just play the lowest possible card
	if (!currentRound || !currentTrick || !winner || !winningCard) {

		const comparison = compareRank(previousCard.rank, nextCard.rank);

		if (comparison < 1) {

			return previousCandidate;
		}

		return nextCandidate;
	}

	//if heart card and will not win the trick, play it
	if (nextCard.suit === Suit.Hearts && winningCard.suit !== Suit.Hearts) {
		//if higher than previous card and is previously is also heart, play it
		const isHigherThanPrevious = compareRank(nextCard.rank, previousCard.rank) > 0;

		if (isHigherThanPrevious && previousCard.suit === Suit.Hearts) {
			return nextCandidate;
		}
	}

	//if at least 2 cards have been played on the trick,
	// check if the played card will not win the trick
	// and is higher than the previous card
	if (currentTrick.cardsPlayed.length >= 2 && winningCard) {
		//check if will win trick
		const nextCardWillWinTrick = compareRank(nextCard.rank, winningCard.rank) > 0 && nextCard.suit === winningCard.suit;
		const previousCardWillWinTrick = compareRank(previousCard.rank, winningCard.rank) > 0 && previousCard.suit === winningCard.suit;

		//if the next card will win the trick
		//and the previous card will not win the trick, play the previous card
		if (nextCardWillWinTrick && !previousCardWillWinTrick) {
			return previousCandidate;
		}

		//if the previous card will win the trick
		//and the next card will not win the trick, play the next card
		if (previousCardWillWinTrick && !nextCardWillWinTrick) {
			return nextCandidate;
		}

		//if both cards will win not the trick, play the one that is higher
		if (!nextCardWillWinTrick && !previousCardWillWinTrick) {
			const isNextCardHigher = compareRank(nextCard.rank, previousCard.rank) > 0;

			if (isNextCardHigher) {
				return nextCandidate;
			}

			return previousCandidate;
		}

	}

	const previousRankPlace = compareRank(previousCard.rank, winningCard.rank);
	const nextRankPlace = compareRank(nextCard.rank, winningCard.rank);

	if (previousRankPlace < nextRankPlace) {
		return previousCandidate;
	}

	return nextCandidate;

}

function getPotentialPoints(command: Command, _gameState: GameState): number {
	// Only care about PlayCommand for trick stage
	if (command.command !== GameAction.PlayCard) {
		return 0;
	}

	const card = getCardObjectFromString((command as PlayCommand).data.card);

	// Hearts are worth 1 point, Queen of Spades is worth 13
	if (card.suit === Suit.Hearts) {
		return 1;
	}

	if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
		return 13;
	}

	return 0;
}

function isSafePlay(_card: CardObject, _gameState: GameState):boolean{
	return true;
}

function getTrickCommand(commands: Command[], gameState: GameState): Command | null {
	// If there are no valid commands, return null
	if (!commands || commands.length === 0) {	
		return null;
	}

	//helpers
	const currentRound = gameState?.rounds?.at(-1);
	const currentTrick = currentRound?.tricks?.at(-1);
	const winner = currentTrick ? getTrickWinner(currentTrick) : null;
	const winningCard = winner !== null && currentTrick?.cardsPlayed[winner] ? getCardObjectFromString(currentTrick?.cardsPlayed[winner]) : null;

	//order all options by possible points from high to low then rank high to low
	commands.sort((a, b) => {
		const aPoints = getPotentialPoints(a, gameState);
		const bPoints = getPotentialPoints(b, gameState);

		if (bPoints !== aPoints) {
			return bPoints - aPoints;
		}

		// If points are equal, sort by rank
		assumeType<PlayCommand>(a);
		assumeType<PlayCommand>(b);
		const aCard = getCardObjectFromString(a.data.card);
		const bCard = getCardObjectFromString(b.data.card);
		return compareRank(bCard.rank, aCard.rank);
	});

	//try to play the highest to lowest point card  
	// with at least 1 point
	// without possible risk of getting points
	for (const command of commands) {
		assumeType<PlayCommand>(command);
		const card = getCardObjectFromString(command.data.card);
		//if the card has no points, skip it
		const potentialPoints = getPotentialPoints(command, gameState);

		if (potentialPoints === 0) {
			continue;
		}

		//is safe to play
		if (winningCard) {
			const willCardWinTrick = compareRank(card.rank, winningCard.rank) > 0 && card.suit === winningCard.suit;

			if (!willCardWinTrick) {
				//if the card will not win the trick, play it
				return command;
			}
		}
	}

	//if can play a different suit from the winning, play the highest card of that suit
	for (const command of commands) {
		assumeType<PlayCommand>(command);
		const card = getCardObjectFromString(command.data.card);

		if (winningCard && card.suit !== winningCard.suit) {
			if (isSafePlay(card, gameState)) {
				return command;
			}
		}
	}

	//play the lowest ranked card
	commands.sort((a, b) => {
		assumeType<PlayCommand>(a);
		assumeType<PlayCommand>(b);
		const aCard = getCardObjectFromString(a.data.card);
		const bCard = getCardObjectFromString(b.data.card);
		return compareRank(aCard.rank, bCard.rank);
	});
	return commands[0] as PlayCommand;

}

export function getMediumBotCommand(game: Manager, position: number, autoAdvanceRound = false): Command | null {

	const validCommands = game.getValidActions(position);

	switch (game.gameState.roundStage) {
		case RoundStage.TrickEnd:
			return makeNextStageCommand();
		case RoundStage.RoundEnd:
			if (autoAdvanceRound) {
				return makeNextStageCommand();
			}

			return getRandomItem(validCommands);
		default:
			return getRandomItem(validCommands);
		case RoundStage.Trick:
			return getTrickCommand(validCommands, game.gameState);
		case RoundStage.Pass:
			return getPassCommand(validCommands);
		case RoundStage.PassEnd:
			if (autoAdvanceRound) {
				return makeNextStageCommand();
			}

			break;
		case RoundStage.Deal:
			if (autoAdvanceRound) {
				return {
					command: GameAction.Deal, 
				} as Command;
			}

			break;
		case RoundStage.GameOver:
			if (autoAdvanceRound) {
				return {
					command: GameAction.NewGame, 
				} as Command;
			}

			break;
	}

	return null;
}

export class MediumBot extends BotBase {
	public getCommand(game: Manager): Command | null {
		return getMediumBotCommand(game, this.position, this.autoAdvanceRound);
	}
}
